import { memo, useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TruckIcon, FlagIcon } from '@heroicons/react/24/outline'
import StewardClaimModal from './StewardClaimModal'
import EntityGalleryStrip from './EntityGalleryStrip'
import EntityImageViewer from './EntityImageViewer'
import EntityScheduleSection from './EntityScheduleSection'
import EntityReviewsSection from '../reviews/EntityReviewsSection'
import EntityMoreInfoSection from './EntityMoreInfoSection'
import TrustBadgesWidget from './TrustBadgesWidget'
import SightingPatternWidget from './SightingPatternWidget'
import SightingTimelineWidget from './SightingTimelineWidget'
import EntityDetailTabs from './EntityDetailTabs'
import ExperiencesTab from '../reviews/ExperiencesTab'

/**
 * EntityExpandedContent - Gallery, tabbed sections, and bottom CTAs for expanded panel.
 * Tabs: Overview (menu + trust signals) / Info (schedule, details, location) / Reviews
 * @atomic @idempotent
 */
const EntityExpandedContent = memo(function EntityExpandedContent({
  entityGallery,
  entityId,
  galleryRenderKey,
  selectedImage,
  selectedImageIndex,
  onImageClick,
  onCloseImage,
  onPreviousImage,
  onNextImage,
  imageUrl,
  primaryImage,
  IconComponent,
  bgColor,
  iconColor,
  handleImageError,
  entityDetails,
  userLocation,
  scheduleRefreshKey,
  handleClaimClick,
  isAlreadySteward,
  onReportClick,
  trustStats,
  sightingPatternCounts,
  peakDow,
  peakHour,
  hourlyBuckets,
  t,
}) {
  const [showClaimModal, setShowClaimModal] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const enrichTab = searchParams.get('enrichTab')
  const [activeTab, setActiveTab] = useState(() => {
    if (enrichTab === 'reviews') return 'reviews'
    if (enrichTab === 'experiences') return 'experiences'
    return 'overview'
  })

  // Clear the enrichTab param after we've acted on it so the URL stays clean
  useEffect(() => {
    if (enrichTab) {
      searchParams.delete('enrichTab')
      setSearchParams(searchParams, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = useCallback((id) => setActiveTab(id), [])

  const tabs = [
    { id: 'overview', label: t('entityDetail.tabs.overview', 'Overview') },
    { id: 'reviews', label: t('entityDetail.tabs.reviews', 'Reviews') },
    { id: 'experiences', label: t('entityDetail.tabs.experiences', 'Experiences') },
  ]

  return (
    <>
      {/* Gallery strip (horizontal scroll) */}
      {entityGallery && entityGallery.length > 0 && !selectedImage && (
        <EntityGalleryStrip
          key={`strip-${entityId}-${galleryRenderKey}`}
          images={entityGallery}
          entityId={entityId}
          onImageClick={onImageClick}
        />
      )}
      {selectedImage && (
        <EntityImageViewer
          selectedImage={selectedImage}
          selectedImageIndex={selectedImageIndex}
          entityGallery={entityGallery}
          onClose={onCloseImage}
          onPrevious={onPreviousImage}
          onNext={onNextImage}
        />
      )}

      {/* Hero image / icon fallback when no gallery */}
      {(!entityGallery || entityGallery.length === 0) && (
        imageUrl ? (
          <div className="mb-2 h-40 bg-primary/5 overflow-hidden relative rounded-lg">
            <img
              key={`hero-${primaryImage?.id || 'default'}`}
              src={imageUrl}
              alt={entityDetails.name}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
            {IconComponent && (
              <div className={`fallback-icon absolute inset-0 ${bgColor} hidden items-center justify-center`}>
                <IconComponent className={`w-24 h-24 ${iconColor}`} />
              </div>
            )}
          </div>
        ) : IconComponent ? (
          <div className={`mb-2 h-40 ${bgColor} flex items-center justify-center rounded-lg`}>
            <IconComponent className={`w-24 h-24 ${iconColor}`} />
          </div>
        ) : null
      )}

      <EntityDetailTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
      />

      {/* Tab: Overview — trust signals, schedule, contact, location */}
      {activeTab === 'overview' && (
        <div className="pt-3 space-y-1 divide-y divide-gray-100">
          <div className="pb-3">
            {(trustStats || sightingPatternCounts) && (
              <div className="px-1">
                {trustStats && (
                  <TrustBadgesWidget
                    totalSightings={trustStats.totalSightings}
                    confirmationRate={trustStats.confirmationRate}
                    activeSince={trustStats.activeSince}
                    spottersToday={trustStats.spottersToday}
                  />
                )}
                {hourlyBuckets?.length ? (
                  <SightingTimelineWidget hourlyBuckets={hourlyBuckets} peakDow={peakDow} peakHour={peakHour} />
                ) : sightingPatternCounts ? (
                  <SightingPatternWidget counts={sightingPatternCounts} />
                ) : null}
              </div>
            )}
          </div>
          <div className="pt-3">
            <EntityScheduleSection key={scheduleRefreshKey} entityId={entityId} t={t} />
          </div>
          <div className="pt-3">
            <EntityMoreInfoSection entityDetails={entityDetails} t={t} />
          </div>
        </div>
      )}

      {/* Tab: Reviews */}
      {activeTab === 'reviews' && (
        <div className="pt-3">
          <EntityReviewsSection entityId={entityId} autoOpenReview={enrichTab === 'reviews'} t={t} />
        </div>
      )}

      {/* Tab: Experiences — official menu + community offerings */}
      {activeTab === 'experiences' && (
        <div className="pt-3">
          <ExperiencesTab
            entityId={entityId}
            entityCategory={entityDetails?.category}
            t={t}
          />
        </div>
      )}

      {/* Claim CTA — hidden when user is already a steward of this entity */}
      {!isAlreadySteward && (
        <button
          type="button"
          onClick={() => setShowClaimModal(true)}
          onTouchEnd={(e) => { e.preventDefault(); setShowClaimModal(true) }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-lg text-xs font-semibold text-primary transition-colors mt-1"
        >
          <TruckIcon className="w-4 h-4 flex-shrink-0" />
          {t('vendor.claim.cta', 'Become a steward')}
        </button>
      )}

      {showClaimModal && (
        <StewardClaimModal
          entityName={entityDetails?.name}
          onConfirm={handleClaimClick}
          onClose={() => setShowClaimModal(false)}
        />
      )}

      {/* Report listing */}
      <button
        type="button"
        onClick={onReportClick}
        onTouchEnd={(e) => { e.preventDefault(); onReportClick() }}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-1"
      >
        <FlagIcon className="w-3.5 h-3.5" />
        {t('report.title', 'Report this listing')}
      </button>
    </>
  )
})

export default EntityExpandedContent
